import { activeRegionAtom } from "@/atoms/activeRegion";
import { homeSpotAtom } from "@/atoms/homeRegion";
import { implicitFloridaHomePath, readLastViewedRegionId } from "@/lib/floridaHome";
import { unitSystemAtom } from "@/atoms/units";
import { trackNavDrawerOpen, trackSettingsUnitChange } from "@/lib/analytics";
import Logo from "@/assets/images/logo.png";
import { useZones, findRegionInZones } from "@/hooks/useRegions";
import MapIcon from "@mui/icons-material/Map";
import MenuIcon from "@mui/icons-material/Menu";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  AppBar,
  Breadcrumbs,
  Button,
  IconButton,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import AuthButton from "./Auth/AuthButton";
import NavigationDrawer from "./NavigationDrawer";

function NavigationBar() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeRegion = useRecoilValue(activeRegionAtom);
  const homeSpot = useRecoilValue(homeSpotAtom);
  const [unitSystem, setUnitSystem] = useRecoilState(unitSystemAtom);
  const { data: zones } = useZones();
  const found = zones ? findRegionInZones(zones, activeRegion) : null;

  return (
    <>
      <AppBar position="sticky">
        <Toolbar
          sx={{
            minHeight: { xs: 48, sm: 52 },
            px: 2,
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="home"
            onClick={() => {
              if (homeSpot) {
                const camPath = homeSpot.cameraId
                  ? `/${encodeURIComponent(homeSpot.cameraId)}`
                  : "";
                const url = `/spot/${encodeURIComponent(homeSpot.regionId)}${camPath}`;
                // Force full page load to guarantee navigation works
                window.location.href = url;
              } else {
                const floridaHome = implicitFloridaHomePath(readLastViewedRegionId());
                navigate(floridaHome ?? "/");
              }
            }}
            sx={{ mr: 1 }}
          >
            <img src={Logo} alt="logo" style={{ height: 28 }} />
          </IconButton>

          {found && (
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  sx={{ fontSize: 12, color: "text.secondary", opacity: 0.5 }}
                />
              }
              sx={{
                flex: 1,
                overflow: "hidden",
                // Mobile: hide entirely — the in-page region dropdown is the
                // source of truth and a duplicate "Algarve" in the toolbar
                // just adds noise. Desktop shows the full Zone › Country ›
                // Region trail where horizontal space exists.
                display: { xs: "none", sm: "flex" },
              }}
            >
              <Typography
                sx={{
                  display: { xs: "none", sm: "inline" },
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  cursor: "pointer",
                  "&:hover": { color: "text.primary" },
                }}
                onClick={() => setDrawerOpen(true)}
              >
                {found.zone.label}
              </Typography>
              <Typography
                sx={{
                  display: { xs: "none", sm: "inline" },
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  cursor: "pointer",
                  "&:hover": { color: "text.primary" },
                }}
                onClick={() => setDrawerOpen(true)}
              >
                {found.country.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "0.8rem" },
                  color: "text.primary",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {found.region.label}
              </Typography>
            </Breadcrumbs>
          )}

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.25, sm: 1 },
            }}
          >
            {/* Desktop-only: units live in the drawer on mobile. */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
              <ToggleButtonGroup
                value={unitSystem}
                exclusive
                onChange={(_, v) => {
                  if (v) {
                    setUnitSystem(v);
                    trackSettingsUnitChange(v);
                  }
                }}
                size="small"
                sx={{
                  height: 26,
                  "& .MuiToggleButton-root": {
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    px: 1,
                    py: 0,
                    color: "text.secondary",
                    borderColor: "divider",
                    "&.Mui-selected": {
                      color: "accent.main",
                      backgroundColor: "transparent",
                    },
                  },
                }}
              >
                <ToggleButton value="metric">°C / m</ToggleButton>
                <ToggleButton value="imperial">°F / ft</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MapIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/surf-spots")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                color: "ink.main",
                borderColor: "rule.strong",
                fontSize: "0.75rem",
                px: 1.25,
                py: 0.25,
                minHeight: 28,
                "& .MuiButton-startIcon": { mr: 0.5 },
                "&:hover": {
                  borderColor: "ink.main",
                  backgroundColor: "transparent",
                },
              }}
            >
              Map
            </Button>
            <Tooltip title="Surf spots map">
              <IconButton
                color="inherit"
                aria-label="Surf spots map"
                onClick={() => navigate("/surf-spots")}
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                <MapIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <AuthButton />
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={() => {
                setDrawerOpen(true);
                trackNavDrawerOpen();
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <NavigationDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}

export default NavigationBar;
